import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Token } from '../models/token.model';
import { AlertsService } from './alerts.service';
import { IBranchExchangeModel } from '../logic/branch/models/branch-exchange.model';
import { Country } from '../models/country.model';
import { Router } from '@angular/router';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly BASE_URL: string = environment.BASE_URL;
  private readonly AUTH_URL: string = environment.AUTH_URL;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _alert: AlertsService,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
  ) {}

  public post<T>(
    url: string,
    body: any,
    extraHeaders?: Record<string, string>,
  ): Observable<T> {
    return this._httpClient
      .post<T>(`${this.BASE_URL}${url}`, body, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          ...extraHeaders,
        },
      })
      .pipe(
        catchError((_) => {
          if (this.isAnonymousUnauthorized(_)) {
            throw _.error;
          }

          const { error } = _;

          if (_.error?.int_code === 10201) {
            throw _.error;
          }

          if (_.error.int_code === 10102) {
            const resendLabel = this._translate.instant(
              'HTTP_SERVICE.RESEND_EMAIL_BUTTON_PREFIX',
            );
            this._alert.showPersonalizado({
              title:
                _.error.title ??
                this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
              text: error?.error?.message ?? _.error.message,
              confirmButtonText: `<span style='color: #000'>${resendLabel} <i class='fa fa-arrow-right'></i></span>`,
              confirmButtonColor: '#00C08B',
              showCancelButton: true,
              cancelButtonText: this._translate.instant(
                'COMMON_ALERTS.BUTTON_CLOSE',
              ),
              background: '#0B2634',
              color: '#fff',
            }).then((result) => {
              if (result.isConfirmed) {
                this.resendVerificationEmail(body.email).subscribe();
              }
            });
          } else if (_.error?.int_code === 10104) {
            this._alert.show({
              title: this._translate.instant(
                'AUTH_REGISTER_ALERTS.CORPORATE_EMAIL_TITLE',
              ),
              text: this._translate.instant(
                'AUTH_REGISTER_ALERTS.CORPORATE_EMAIL_TEXT',
              ),
              confirmButtonText: this._translate.instant(
                'COMMON_ALERTS.BUTTON_OK',
              ),
            });
          } else if (error?.error?.message) {
            this._alert.show({
              title:
                _.error.title ??
                this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
              text: error.error.message,
              confirmButtonText: this._translate.instant(
                'COMMON_ALERTS.BUTTON_OK',
              ),
            });
          } else {
            this._alert.show({
              title:
                _.error.title ??
                this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
              text: _.error.message,
              confirmButtonText: this.errorConfirmButtonText(_),
            });
          }

          this.redirectOnExpiredSession(_);
          throw _.error;
        })
      );
  }

  public patch<T>(url: string, body: any): Observable<T> {
    return this._httpClient
      .patch<T>(`${this.BASE_URL}${url}`, body, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .pipe(
        catchError((_) => {
          if (this.isAnonymousUnauthorized(_)) {
            throw _.error;
          }
          this._alert.show({
            title:
              _.error.title ??
              this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: _.error.message,
            confirmButtonText: this.errorConfirmButtonText(_),
          });
          this.redirectOnExpiredSession(_);
          throw _.error;
        })
      );
  }

  public delete<T>(url: string): Observable<T> {
    return this._httpClient
      .delete<T>(`${this.BASE_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .pipe(
        catchError((_) => {
          if (this.isAnonymousUnauthorized(_)) {
            throw _.error;
          }
          this._alert.show({
            title:
              _.error.title ??
              this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: _.error.message,
            confirmButtonText: this.errorConfirmButtonText(_),
          });
          this.redirectOnExpiredSession(_);
          throw _.error;
        })
      );
  }

  public put<T>(url: string, body: any = {}): Observable<T> {
    return this._httpClient
      .put<T>(`${this.BASE_URL}${url}`, body, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .pipe(
        catchError((_) => {
          if (this.isAnonymousUnauthorized(_)) {
            throw _.error;
          }
          this._alert.show({
            title:
              _.error.title ??
              this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: _.error.message,
            confirmButtonText: this.errorConfirmButtonText(_),
          });
          this.redirectOnExpiredSession(_);
          throw _.error;
        })
      );
  }

  public get<T>(url: string): Observable<T> {
    return this._httpClient
      .get<T>(`${this.BASE_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .pipe(
        catchError((_) => {
          if (this.isAnonymousUnauthorized(_)) {
            throw _.error;
          }
          this._alert.show({
            title:
              _.error.title ??
              this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: _.error.message,
            confirmButtonText: this.errorConfirmButtonText(_),
          });
          console.error(url, _.error);
          this.redirectOnExpiredSession(_);
          throw _.error;
        })
      );
  }

  clearSessionStorage() {
    Object.entries(sessionStorage).forEach(([key, val]) => {
      if (key != 'cotizacion') delete sessionStorage[key];
    });
  }

  private hasAuthToken(): boolean {
    const token = this.getToken();
    return !!token && token !== 'null' && token !== 'undefined';
  }

  private isUnauthorized(httpError: HttpErrorResponse | any): boolean {
    return (
      httpError?.status === 401 || httpError?.error?.statusCode === 401
    );
  }

  private isAnonymousUnauthorized(httpError: HttpErrorResponse | any): boolean {
    return this.isUnauthorized(httpError) && !this.hasAuthToken();
  }

  private errorConfirmButtonText(httpError: HttpErrorResponse | any): string {
    if (this.isUnauthorized(httpError) && this.hasAuthToken()) {
      return this._translate.instant('AUTH_CODE.GO_TO_LOGIN');
    }
    return this._translate.instant('COMMON_ALERTS.BUTTON_ACCEPT');
  }

  private redirectOnExpiredSession(httpError: HttpErrorResponse | any): void {
    if (!this.isUnauthorized(httpError) || !this.hasAuthToken()) {
      return;
    }
    this.clearSessionStorage();
    this._router.navigate(['auth', 'login']);
  }

  public signIn(
    dataLoging: Partial<{ email: null; password: null }>
  ): Observable<any> {
    return this._httpClient.post(this.AUTH_URL, dataLoging).pipe(
      map((response: Token | any) => {
        if (!response.token) throw new Error(response.error);
        return response.token;
      })
    );
  }

  public saveToken(token: string) {
    localStorage.setItem('token', token);
  }

  public getToken() {
    return localStorage.getItem('token');
  }

  public getBranches() {
    return this._httpClient.get(`${this.BASE_URL}/branches`);
  }

  public getBranchesCurrency(
    id_branche: number
  ): Observable<IBranchExchangeModel> {
    return this._httpClient.get<IBranchExchangeModel>(
      `${this.BASE_URL}/branches/${Number(id_branche)}/exchanges`
    );
  }

  public getCurrency() {
    return this._httpClient.get(this.BASE_URL).pipe();
  }

  public getPromotions() {
    return this._httpClient.get(`${this.BASE_URL}/promotions`);
  }

  public getPromotionAvailability(promotionId: number) {
    return this._httpClient.get(
      `${this.BASE_URL}/promotions/${promotionId}/availability`,
    );
  }

  public getCountries(): Observable<Country[]> {
    return this._httpClient
      .get<{ success?: boolean; data?: Country[] }>(`${this.BASE_URL}/countries`)
      .pipe(map((response: any) => response?.data ?? response ?? []));
  }

  /**
   * Active legal document from GET /legal-documents/:type (ApiResponse.data = toPublicPayload).
   * Full HTML is in `data.content`; `change_summary` is only metadata.
   */
  private extractLegalDocumentHtmlFromApiResponse(response: unknown): string {
    if (response == null || typeof response !== 'object') {
      return '';
    }
    const root = response as { data?: unknown };
    const pkg = root.data ?? response;
    if (typeof pkg === 'string') {
      return pkg;
    }
    if (pkg == null || typeof pkg !== 'object') {
      return '';
    }
    const row = pkg as { content?: unknown; body?: unknown };
    if (typeof row.content === 'string') {
      return row.content;
    }
    if (typeof row.body === 'string') {
      return row.body;
    }
    return '';
  }

  public getTYC() {
    return this._httpClient
      .get(`${this.BASE_URL}/legal-documents/terms_and_conditions`)
      .pipe(
        map((response: any) => {
          const html = this.extractLegalDocumentHtmlFromApiResponse(response);
          const d = response?.data;
          if (d != null && typeof d === 'object') {
            return { ...d, body: html };
          }
          return { body: html };
        }),
      );
  }

  public getBanner() {
    return this._httpClient
      .get(`${this.BASE_URL}/pages/banner`)
      .pipe(map((response: any) => response?.data ?? response));
  }

  public getAvisoDePrivacidad() {
    return this._httpClient
      .get(`${this.BASE_URL}/legal-documents/privacy_policy`)
      .pipe(
        map((response: any) => {
          const html = this.extractLegalDocumentHtmlFromApiResponse(response);
          const d = response?.data;
          if (d != null && typeof d === 'object') {
            return { ...d, body: html };
          }
          return { body: html };
        }),
      );
  }

  /** Public endpoint (no auth). Used by landing for device mockup image. */
  public getSettingByKey(key: string): Observable<string | null> {
    return this._httpClient
      .get<{ data?: { items?: string } }>(`${this.BASE_URL}/settings/${key}`)
      .pipe(
        map((res) => res?.data?.items ?? null),
        catchError(() => of(null)),
      );
  }

  public getNotificationPreferences() {
    return this.get<{
      success: boolean;
      data?: {
        reservationStatus: { push: boolean; sms: boolean };
        securityAlerts: { email: boolean; push: boolean };
        priceAlerts: { push: boolean };
        weeklyMarket: { email: boolean };
        rewards: { push: boolean };
        referrals: { push: boolean };
      };
    }>('/notifications/preferences');
  }

  public updateNotificationPreferences(body: Record<string, unknown>) {
    return this.patch<{
      success: boolean;
      data?: {
        reservationStatus: { push: boolean; sms: boolean };
        securityAlerts: { email: boolean; push: boolean };
        priceAlerts: { push: boolean };
        weeklyMarket: { email: boolean };
        rewards: { push: boolean };
        referrals: { push: boolean };
      };
    }>('/notifications/preferences', body);
  }

  public getCurrentUser(token: string = '') {
    return this.get('/users/current').pipe(
      map((response: any) => {
        // El backend devuelve ApiResponse con data.items
        if (response.success && response.data?.items) {
          const user = response.data.items;
          // Format birthday for display
          if (user.birthday) {
            user.birthday = moment(user.birthday).format('DD/MM/YYYY');
          }
          localStorage.setItem('current', JSON.stringify(user));
          return user;
        }
        throw new Error(
          this._translate.instant('HTTP_SERVICE.GET_CURRENT_USER_FAILED'),
        );
      })
    );
  }

  public getReservations(idClient: number) {
    return this.get(`/reservations/client/${idClient}`).pipe(
      map((res: any) => {
        localStorage.setItem('reservations', JSON.stringify(res));
        return res;
      })
    );
  }

  public getBranchHistoryCurrency() {
    return of([]);
    let branch_id = JSON.parse(localStorage.getItem('branchId') as string);

    return this.get(`/branches/${branch_id}/exchanges/histories`).pipe(
      map((res: any) => {
        sessionStorage.setItem('currency_history', JSON.stringify(res));
        return res;
      })
    );
  }

  public getBranchHistoryCurrencyId(branch_id: number) {
    return of([]);
    return this.get(`/branches/${branch_id}/exchanges/histories`).pipe(
      map((res: any) => {
        sessionStorage.setItem('currency_history', JSON.stringify(res));
        return res;
      })
    );
  }

  public newPassword(body: any) {
    return this.post(`/auth/resetPassword`, body).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  /**
   * Cambio de contraseña para usuario autenticado (no confundir con resetPassword).
   */
  public changePassword(body: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<any> {
    return this._httpClient
      .post(`${this.BASE_URL}/auth/password`, body, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          const payload = httpError.error;
          this.redirectOnExpiredSession(httpError);
          return throwError(() => httpError);
        }),
        map((res: any) => res),
      );
  }

  public updateCurrentUser(user_id: number, body: any) {
    return this.patch(`/users/${user_id}`, body).pipe(
      map((res: any) => {
        if (res.success && res.data?.items) {
          localStorage.setItem('current', JSON.stringify(res.data.items));
        }
        return res;
      })
    );
  }

  /** Multipart upload for the authenticated user's avatar (JPEG/PNG/WEBP). */
  public uploadAvatar(file: File): Observable<{ avatar: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.post<any>('/users/avatar', formData).pipe(
      map((res: any) => {
        const avatar = res?.data?.items?.avatar as string | undefined;
        if (!res?.success || !avatar) {
          throw new Error(
            res?.message ||
              this._translate.instant('PROFILE_PAGE.AVATAR_UPLOAD_FAILED'),
          );
        }

        try {
          const current = JSON.parse(localStorage.getItem('current') || '{}');
          current.avatar = avatar;
          localStorage.setItem('current', JSON.stringify(current));
        } catch {
          // ignore storage errors
        }

        return { avatar };
      }),
    );
  }

  public getAccountSettings() {
    return this.get<any>('/users/account-settings');
  }

  public updateAccountSettings(body: {
    appearance?: 'light' | 'dark' | 'system';
    language?: 'es' | 'en';
    twoFactorEnabled?: boolean;
    favoriteBranchId?: number;
  }) {
    return this.patch<any>('/users/account-settings', body);
  }

  public getAccountDevices() {
    return this.get<any>('/users/account-devices');
  }

  public revokeAccountDevice(deviceId: number) {
    return this.delete<any>(`/users/account-devices/${deviceId}`);
  }

  public exportAccountData() {
    return this.get<any>('/users/account-export');
  }

  public deleteAccount() {
    return this.delete<any>('/users/account');
  }

  public completeMagicLink(body: {
    token: string;
    confirmQuotationIds?: number[];
  }) {
    return this._httpClient
      .post<any>(`${this.BASE_URL}/auth/magic-link`, body)
      .pipe(
        catchError((httpError: HttpErrorResponse) =>
          throwError(() => httpError.error ?? httpError),
        ),
      );
  }

  public updateUserLanguage(language: 'es' | 'en') {
    return this.patch('/users/language', { language }).pipe(
      map((res: any) => {
        if (res.success && res.data?.items) {
          localStorage.setItem('current', JSON.stringify(res.data.items));
        }
        return res;
      }),
    );
  }

  public requestPassword(body: any) {
    return this.post(`/auth/recoveryPassword`, body).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public registrarUsuario(body: any) {
    return this.post(`/auth/register`, body).pipe(
      map((res: any) => {
        sessionStorage.setItem('currency_history', JSON.stringify(res));
        return res;
      })
    );
  }

  public crearReservacion(body: any) {
    return this.post(`/reservations`, body).pipe(
      map((res: any) => {
        sessionStorage.setItem('cotizacion', JSON.stringify(res));
        return res;
      })
    );
  }

  public crearReservacionGetMoney(body: any) {
    return this.post(`/reservations/getmoney`, body).pipe(
      map((res: any) => {
        sessionStorage.setItem('cotizacion', JSON.stringify(res));
        return res;
      })
    );
  }

  public setBanner(body: any) {
    return this.post(`/pages/banner`, body).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public cotizar(body: any) {
    return this.post(`/cotizacion`, body).pipe(
      map((res: any) => {
        sessionStorage.setItem('cotizacion', JSON.stringify(res));
        return res;
      })
    );
  }

  cancelarReservacion(reservacion_id: number) {
    return this.get(`/reservations/${reservacion_id}/cancel`);
  }

  public assignUserBranch(userId: number, branchId: number) {
    return this.put(`/users/branch/${userId}/${branchId}`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public getUserBranches(userId: number) {
    return this.get(`/users/${userId}/branches`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public resendVerificationEmail(email: string) {
    return this._httpClient
      .post(`${this.BASE_URL}/auth/resendVerificationEmail`, { email })
      .pipe(
        map((res: any) => {
          this._alert.show({
            title: this._translate.instant('HTTP_SERVICE.VERIFICATION_SENT_TITLE'),
            icon: 'success',
            text: this._translate.instant(
              'HTTP_SERVICE.VERIFICATION_SENT_TEXT',
            ),
            confirmButtonText: this._translate.instant(
              'COMMON_ALERTS.BUTTON_OK',
            ),
          });
          return res;
        }),
        catchError((_) => {
          throw _.error;
        })
      );
  }

  /** Solicita OTP por SMS o WhatsApp para verificar / cambiar teléfono (JWT). */
  public sendPhoneOtp(phone: string, channel: 'sms' | 'whatsapp' = 'sms') {
    const phoneDigits = String(phone ?? '').replace(/\D/g, '');
    return this._httpClient
      .post(
        `${this.BASE_URL}/auth/sendPhoneOtp`,
        { phone: phoneDigits, channel },
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      )
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          this.redirectOnExpiredSession(httpError);
          return throwError(() => httpError);
        }),
        map((res: any) => res),
      );
  }

  /** Valida el OTP de teléfono y aplica el número pendiente (JWT). */
  public verifyPhoneOtp(code: number | string) {
    return this._httpClient
      .post(
        `${this.BASE_URL}/auth/verifyPhoneOtp`,
        { code: Number(code) },
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      )
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          this.redirectOnExpiredSession(httpError);
          return throwError(() => httpError);
        }),
        map((res: any) => res),
      );
  }

  /** Reclama el bono de +500 pts por perfil completo (idempotente, JWT). */
  public claimProfileCompleteBonus() {
    return this._httpClient
      .post(
        `${this.BASE_URL}/auth/claimProfileCompleteBonus`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      )
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          this.redirectOnExpiredSession(httpError);
          return throwError(() => httpError);
        }),
        map((res: any) => res),
      );
  }

  public getReferralInviteStatus() {
    return this.get<any>('/referrals/me');
  }

  public getMyReferrals(page = 1, perPage = 50) {
    return this.get<any>(`/referrals/me/list?page=${page}&perPage=${perPage}`);
  }

  public getReferralPreview(code: string) {
    return this._httpClient
      .get<any>(
        `${this.BASE_URL}/referrals/preview/${encodeURIComponent(code)}`,
      )
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          return throwError(() => httpError);
        }),
      );
  }

  public sendReferralInvite(
    channel: 'whatsapp' | 'email',
    destination: string,
  ) {
    return this.post<any>('/referrals/invite/resend', { channel, destination });
  }
}
